import { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import Input from "shared/Input/Input";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { Link, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { Disclosure } from "@headlessui/react";
import { FaAngleDown, FaAngleUp } from "react-icons/fa";
import Label from "components/Label/Label";
import { State } from "country-state-city";
import Select from "react-select";
import axios from "axios";
import appConfig from "configs/app.config";
import React, { FC, useEffect, useState } from "react";
import Cookies from "js-cookie";
import {
  loadAcceptJs,
  tokenizeCardWithAcceptJs,
  tokenizeCardWithAcceptJsWithCredentials,
  getBasilCredentials,
  parseExpiration,
} from "utils/acceptJs";
export interface PageSignUpProps {
  className?: string;
}

const PageSignUp: FC<PageSignUpProps> = ({ className = "" }) => {
  const stateOption = useMemo(() => {
    return State.getAllStates()
      .filter((code) => code.countryCode === "US")
      .map((state) => ({
        label: state.name,
        value: state.isoCode,
      }));
  }, []);
  const cleanPhoneNumber = (phoneNumber: string) => {
    if (phoneNumber) {
      return phoneNumber.replace(/\D/g, "");
    } else {
      return null;
    }
  };
  const formatOptionLabel = ({ label, value }: any, { context }: any) => {
    return context === "value" ? value : label;
  };
  const isDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      password: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      cardNumber: "",
      cardName: "",
      exprDate: "",
      CVC: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      country: "USA",
      zipCode: "",
    },
  });
  const [errorMessage, setErrorMessage] = useState("");
  const isDark = Cookies.get("theme");
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage("");
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);
  const navigate = useNavigate();

  const MERCHANT_STORAGE_KEY = "merchantname";
  const merchantname = Cookies.get(MERCHANT_STORAGE_KEY);
  const getCurrentTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };
  const submitform = async (data: any) => {
    Cookies.remove("CustomerInfo");
    Cookies.remove("userDataaInfo");
    Cookies.remove("addedPayments");
    Cookies.remove("BillingAddress");
    Cookies.remove("checkTime");
    Cookies.remove("fromTime");
    Cookies.remove("toTime");
    Cookies.remove("user");
    Cookies.remove("SignupData");
    Cookies.remove("customerAdded");
    Cookies.remove("paymentId");
    Cookies.remove("loginTime");
    Cookies.remove("orderFee");
    Cookies.remove("paymentId");
    Cookies.remove("orderFeeLabel");
    Cookies.remove("phone");
    sessionStorage.removeItem("onlineOrderSettingsLogo");
    const basicInfo = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: cleanPhoneNumber(data.phone),

      password: data.password,
    };
    const [first, last] = (data?.cardName || "").trim().split(/\s+/);
    const billingFirstName = (first || data.firstName || "").trim() || data.firstName;
    const billingLastName = (last || data.lastName || "").trim() || data.lastName;
    const hasCard = data?.cardNumber?.replace(/\s/g, "");
    let merchantOpaqueData: { dataDescriptor: string; dataValue: string } | null = null;
    let basilOpaqueData: { dataDescriptor: string; dataValue: string } | null = null;
    if (hasCard) {
      try {
        await loadAcceptJs();
        if (typeof window.Accept === "undefined") {
          setErrorMessage("Payment script did not load. Please try again.");
          return;
        }
        const { month: expMonth, year: expYear } = parseExpiration(
          data.exprDate || ""
        );
        const cardNumber = data.cardNumber.replace(/\s/g, "");
        const cvc = data.CVC || "";
        merchantOpaqueData = await tokenizeCardWithAcceptJs(
          cardNumber,
          expMonth,
          expYear,
          cvc
        );
        await loadAcceptJs({ useProdUrl: true });
        basilOpaqueData = await tokenizeCardWithAcceptJsWithCredentials(
          cardNumber,
          expMonth,
          expYear,
          cvc,
          getBasilCredentials()
        );
      } catch (err: any) {
        setErrorMessage(err?.message || "Payment tokenization failed.");
        return;
      }
    }
    // Backend expects: basicInfo, billingInfo (separate), paymentInfo with merchant + basil tokens when card present
    const billingInfo = {
      firstName: billingFirstName,
      lastName: billingLastName,
      address: `${data.address1 || ""}${data.address2 || ""}`.trim() || undefined,
      city: data.city,
      state: data.state,
      zipCode: data.zipCode,
      country: data.country,
      addressType: "Billing",
    };
    const paymentInfo: Record<string, unknown> = {
      customerProfileId: "",
    };
    if (merchantOpaqueData && basilOpaqueData) {
      paymentInfo.merchantDataDescriptor = merchantOpaqueData.dataDescriptor;
      paymentInfo.merchantDataValue = merchantOpaqueData.dataValue;
      paymentInfo.basilDataDescriptor = basilOpaqueData.dataDescriptor;
      paymentInfo.basilDataValue = basilOpaqueData.dataValue;
    } else if (merchantOpaqueData) {
      paymentInfo.dataDescriptor = merchantOpaqueData.dataDescriptor;
      paymentInfo.dataValue = merchantOpaqueData.dataValue;
    }
    const cardSave = hasCard ? "True" : "False";
    try {
      const apiurlmerchant = "/onlineOrder/customer";
      const baseurl = appConfig.apiPrefix;
      const response = await axios.post(
        baseurl + apiurlmerchant,
        { basicInfo, billingInfo, paymentInfo, defaultPaymentProfile: false },
        {
          headers: {
            MID: merchantname,
            cardSave: cardSave,
            Timezone: getCurrentTimezone(),
          },
        }
      );

      const resData = response.data as {
        customerDTO?: any;
        NewAuthNetProfile?: { customerProfileId?: string; customerPaymentProfileIdList?: { numericString?: string[] } };
        NewBasilAuthNetProfile?: { customerProfileId?: string; customerPaymentProfileIdList?: { numericString?: string[] } };
      };
      const customerAdded = resData.customerDTO;
      const newAuthNet = resData.NewAuthNetProfile;
      const newBasilAuthNet = resData.NewBasilAuthNetProfile;

      if (response.data && customerAdded) {
        const basilProfileId = newBasilAuthNet?.customerProfileId ?? newAuthNet?.customerProfileId ?? customerAdded?.authNetProfileId;
        const firstPaymentProfileId =
          newBasilAuthNet?.customerPaymentProfileIdList?.numericString?.[0] ??
          newAuthNet?.customerPaymentProfileIdList?.numericString?.[0];

        let paymentArr: any[] = [];
        if (hasCard && data?.cardNumber) {
          paymentArr = [
            {
              billTo: {
                customerId: data.customerId,
                address: (data.address1 + " " + (data.address2 || "")).trim(),
                city: data.city,
                country: data.country,
                email: data.email,
                firstName: data.firstName,
                lastName: data.lastName,
                phoneNumber: data.phone,
                state: data.state,
                zip: data.zipCode,
              },
              payment: {
                creditCard: {
                  cardNumber: "XXXX" + String(data.cardNumber).slice(-4),
                  cardName: data.cardName,
                },
              },
            },
          ];
        }
        const item = {
          "AuthNet Profile Info ": {
            profile: { paymentProfiles: paymentArr },
          },
          customerProfileId: basilProfileId,
          paymentProfiles: paymentArr,
        };

        Cookies.set("CustomerInfo", JSON.stringify(customerAdded));
        Cookies.set("userDataaInfo", JSON.stringify(item));
        Cookies.set("SignupData", JSON.stringify(customerAdded));
        Cookies.set("customerAdded", JSON.stringify(customerAdded));
        Cookies.set(
          "paymentId",
          JSON.stringify(
            firstPaymentProfileId != null
              ? { customerPaymentProfileId: firstPaymentProfileId }
              : { customerProfileId: basilProfileId }
          )
        );
        Cookies.set("user", basilProfileId != null ? String(basilProfileId) : "");
        Cookies.set("addedPayments", JSON.stringify(paymentArr));
        if (customerAdded?.token) {
          Cookies.set("authToken", customerAdded.token);
        }
        navigate(`/${merchantname}`);
        
      }
       window.location.reload();
    } catch (error) {
      console.log(error);
      const ErrorResponse: any = error;
      setErrorMessage(ErrorResponse.response.data.message);
      console.error("Login Failed", error);
    }
  };

  const validateCardNumber = (values: any) => {
    const cardNumber = values.replace(/\s/g, ""); // Remove all spaces

    if (!cardNumber) {
      return "Card number is required";
    } else if (cardNumber.length < 13) {
      return "Card number must be at least 13 digits";
    } else if (cardNumber.length > 16) {
      return "Card number must be no more than 16 digits";
    } else {
      return true;
    }
  };

  const validateData = (value: any) => {
    if (!/^\d{2}\/\d{2}$/.test(value)) {
      return "Expiration date must be in MM/YY format";
    }
    const [month, year] = value.split("/").map(Number);
    // if (month < 1 || month > 12) {
    //   return "Expiration month must be between 01 and 12";
    // }

    const currentYear = new Date().getFullYear() % 100;
    const currentMonth = new Date().getMonth() + 1;

    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      return "Invalid Expiration date";
    } else {
      return true;
    }
  };

  const formatPhoneNumber = (phoneNumber: string) => {
    const cleaned = phoneNumber.replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

    if (phoneNumber) {
      setValue("phone", phoneNumber);
    }
    if (match) {
      return `(${match[1]})${match[2]}-${match[3]}`;
    }
    return phoneNumber;
  };

  return (
    <div className={`nc-PageSignUp  ${className}`} data-nc-id="PageSignUp">
      <Helmet>
        <title>Basil POS Online Ordering</title>
      </Helmet>
      <div className="container mb-14 lg:mb-32">
        <h2 className="my-10 flex items-center text-3xl leading-[115%] md:text-5xl md:leading-[115%] font-semibold text-neutral-900 dark:text-neutral-100 justify-center">
          Signup
        </h2>
        <div className="max-w-md mx-auto space-y-3 ">
          <div
            className={`${
              errorMessage &&
              "bg-red-100 border text-red-700 px-4 py-3 rounded relative"
            }`}
            role="alert"
          >
            <span className="block sm:inline">{errorMessage}</span>
          </div>
          {/* FORM */}
          <form
            onSubmit={handleSubmit(submitform)}
            className="grid grid-cols-1 gap-6"
          >
            <div className="block">
              <span className="text-neutral-800 dark:text-neutral-200">
                <span className="text-red-500">*</span>
                First Name
              </span>
              <Controller
                name="firstName"
                control={control}
                rules={{
                  required: "First Name is required",
                }}
                render={({ field }) => (
                  <Input
                    type="text"
                    {...field}
                    className="mt-1"
                    placeholder="enter first name"
                  />
                )}
              />
              {errors.firstName && (
                <span className="text-red-500">{errors.firstName.message}</span>
              )}
            </div>
            <div className="block">
              <span className="text-neutral-800 dark:text-neutral-200">
                <span className="text-red-500">*</span>
                Last Name
              </span>
              <Controller
                name="lastName"
                control={control}
                rules={{
                  required: "Last Name is required",
                }}
                render={({ field }) => (
                  <Input
                    type="text"
                    {...field}
                    className="mt-1"
                    placeholder="enter last name"
                  />
                )}
              />
              {errors.lastName && (
                <span className="text-red-500">{errors.lastName.message}</span>
              )}
            </div>
            <div className="block">
              <span className="text-neutral-800 dark:text-neutral-200">
                <span className="text-red-500">*</span>
                Email Address
              </span>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid Email!!",
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    className="mt-1"
                    placeholder="enter email address"
                  />
                )}
              />
              {errors.email && (
                <span className="text-red-500">{errors.email.message}</span>
              )}
            </div>
            <div className="block">
              <span className="text-neutral-800 dark:text-neutral-200">
                <span className="text-red-500">*</span>
                Phone Number
              </span>
              <Controller
                name="phone"
                control={control}
                rules={{
                  required: "Phone Number is required",
                  validate: (values) => {
                    const cleaned = values.replace(/\D/g, "");
                    const formet = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
                    if (!formet) {
                      return "Invalid Format";
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <Input
                    type="tel"
                    {...field}
                    className="mt-1"
                    placeholder="(000)000-0000"
                    onChange={(e: any) => {
                      const values = e.target.value;
                      const formatted = formatPhoneNumber(values);
                      setValue("phone", formatted);
                      trigger("phone");
                    }}
                  />
                )}
              />
              {errors.phone && (
                <span className="text-red-500">{errors.phone.message}</span>
              )}
            </div>

            <div className="block">
              <span className="text-neutral-800 dark:text-neutral-200">
                <span className="text-red-500">*</span>
                Password
              </span>
              <Controller
                name="password"
                control={control}
                rules={{
                  required: "password is required",
                }}
                render={({ field }) => (
                  <Input
                    type="password"
                    {...field}
                    className="mt-1"
                    placeholder="enter password"
                  />
                )}
              />
              {errors.password && (
                <span className="text-red-500">{errors.password.message}</span>
              )}
            </div>
            <div className="max-w-lg">
              <Disclosure>
                {({ open }) => (
                  <>
                    <Disclosure.Button className="flex items-center justify-between w-full px-4 py-2 font-medium text-right bg-slate-100/80 hover:bg-slate-200/60 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-lg focus:outline-none focus-visible:ring focus-visible:ring-slate-500 focus-visible:ring-opacity-75 ">
                      Billing Address
                      {!open ? (
                        <FaAngleDown className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                      ) : (
                        <FaAngleUp className="w-4 h-4 tezxt-slate-600 dark:text-slate-400" />
                      )}
                    </Disclosure.Button>
                    <Disclosure.Panel
                      className="p-4 pt-3 last:pb-0 text-slate-600 text-sm dark:text-slate-300"
                      as="div"
                    >
                      <div
                        className={`mt-6 mb-4 w-full space-y-3 sm:space-y-5`}
                      >
                        <div className="max-w-lg">
                          <Label className="text-sm"> Address 1</Label>
                          <Controller
                            name="address1"
                            control={control}
                            rules={{
                              required: "Address is Required",
                            }}
                            render={({ field }) => (
                              <Input
                                {...field}
                                placeholder="address1"
                                className="mt-1.5"
                              />
                            )}
                          />
                          {errors.address1 && (
                            <span className="text-red-500">
                              {errors.address1.message}
                            </span>
                          )}
                        </div>
                        <div className="max-w-lg">
                          <Label className="text-sm">Address 2</Label>
                          <Controller
                            name="address2"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                placeholder=""
                                className="mt-1.5"
                              />
                            )}
                          />
                        </div>
                        <div className="flex flex-col sm:flex-row sm:space-y-0 sm:space-x-3">
                          <div className="flex-1">
                            <Label className="text-sm">City</Label>
                            <Controller
                              name="city"
                              control={control}
                              rules={{
                                required: "City is Required",
                              }}
                              render={({ field }) => (
                                <Input {...field} className="mt-1.5" />
                              )}
                            />
                            {errors.city && (
                              <span className="text-red-500">
                                {errors.city.message}
                              </span>
                            )}
                          </div>
                          <div className="flex-1">
                            <Label className="text-sm">State</Label>
                            <Controller
                              name="state"
                              control={control}
                              rules={{
                                required: "State is Required",
                              }}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  className="mt-1.5 input-focus-disable"
                                  options={stateOption}
                                  isSearchable
                                  value={stateOption.find(
                                    (option) => option.value === field.value
                                  )}
                                  onChange={(e: any) => {
                                    setValue("state", e.value);
                                    clearErrors("state");
                                  }}
                                  formatOptionLabel={formatOptionLabel}
                                  styles={{
                                    control: (provided) => ({
                                      ...provided,
                                      backgroundColor: "transparent",
                                      height: "48px",
                                      overflow: "auto",
                                    }),
                                    option: (provided, state) => ({
                                      ...provided,
                                      backgroundColor:
                                        isDark === "dark" ||
                                        (isDarkMode === true &&
                                          isDark === "system")
                                          ? state.isFocused
                                            ? "#000000" // Light gray for focus in dark mode
                                            : "#374151" // Dark background in dark mode
                                          : state.isFocused
                                          ? "#DEEBFF" // Light blue for focus in light mode
                                          : "#ffffff", // **Ensure background is white in light mode**
                                      color:
                                        isDark === "dark" ||
                                        (isDarkMode === true &&
                                          isDark === "system")
                                          ? state.isSelected
                                            ? "#ffffff" // Selected text color in dark mode
                                            : "#f5f5f5"
                                          : state.isSelected
                                          ? "#000000" // **Ensure selected text is black in light mode**
                                          : "#333333", // Default text color in light mode
                                    }),
                                    singleValue: (provided) => ({
                                      ...provided,
                                      color:
                                        isDark === "dark" ||
                                        (isDarkMode === true &&
                                          isDark === "system")
                                          ? "white"
                                          : "#000000", // Selected value text color
                                    }),
                                  }}
                                />
                              )}
                            />
                            {errors.state && (
                              <span className="text-red-500">
                                {errors.state.message}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:space-y-0 sm:space-x-3">
                          <div className="flex-1">
                            <Label className="text-sm">Country</Label>
                            <Controller
                              name="country"
                              control={control}
                              rules={{
                                required: "Country is Required",
                              }}
                              render={({ field }) => (
                                <Input {...field} className="mt-1.5" />
                              )}
                            />
                            {errors.country && (
                              <span className="text-red-500">
                                {errors.country.message}
                              </span>
                            )}
                          </div>
                          <div className="flex-1">
                            <Label className="text-sm">Zipcode</Label>
                            <Controller
                              name="zipCode"
                              control={control}
                              rules={{
                                required: "Zipcode is Required",
                              }}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  className="mt-1.5"
                                  placeholder="enter zipcode"
                                  onInput={(e: any) => {
                                    e.target.value = e.target.value
                                      .replace(/\D/g, "")
                                      .slice(0, 6);
                                  }}
                                />
                              )}
                            />
                            {errors.zipCode && (
                              <span className="text-red-500">
                                {errors.zipCode.message}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            </div>
            <div className="max-w-lg">
              <Disclosure>
                {({ open }) => (
                  <>
                    <Disclosure.Button className="flex items-center justify-between w-full px-4 py-2 font-medium text-right bg-slate-100/80 hover:bg-slate-200/60 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-lg focus:outline-none focus-visible:ring focus-visible:ring-slate-500 focus-visible:ring-opacity-75 ">
                      Payment Method
                      {!open ? (
                        <FaAngleDown className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                      ) : (
                        <FaAngleUp className="w-4 h-4 tezxt-slate-600 dark:text-slate-400" />
                      )}
                    </Disclosure.Button>
                    <Disclosure.Panel
                      className="p-4 pt-3 last:pb-0 text-slate-600 text-sm dark:text-slate-300"
                      as="div"
                    >
                      <div
                        className={`mt-6 mb-4 w-full space-y-3 sm:space-y-5`}
                      >
                        <div className="max-w-lg">
                          <Label className="text-sm">Card Number</Label>
                          <Controller
                            name="cardNumber"
                            control={control}
                            rules={{
                              required: "Card number is required",
                              validate: validateCardNumber,
                            }}
                            render={({ field }) => (
                              <Input
                                {...field}
                                className="mt-1.5"
                                type="tel"
                                placeholder="enter card number"
                                onInput={(e: any) => {
                                  e.target.value = e.target.value.replace(
                                    /\D/g,
                                    ""
                                  );
                                }}
                              />
                            )}
                          />
                          {errors.cardNumber && (
                            <span className="text-red-500">
                              {errors.cardNumber.message}
                            </span>
                          )}
                        </div>
                        <div className="max-w-lg">
                          <Label className="text-sm">Name on Card</Label>
                          <Controller
                            name="cardName"
                            control={control}
                            render={({ field }) => (
                              <Input type="text" {...field} className="mt-1" />
                            )}
                          />
                          {errors.cardName && (
                            <span className="text-red-500">
                              {errors.cardName.message}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                        <div className="sm:w-2/3">
  <Label className="text-sm">
    <span className="text-red-500">*</span> Expiration Date (MM/YY)
  </Label>
  <Controller
    name="exprDate"
    control={control}
    rules={{
      required: "Expiration date is required",
      validate: validateData,
    }}
    render={({ field }) => (
      <Input
        {...field}
        className="mt-1.5"
        type="tel"
        maxLength={5}
        placeholder="MM/YY"
        onChange={(e: any) => {
          let value = e.target.value.replace(/\D/g, ""); // Remove non-digits

          if (value.length === 1 && parseInt(value) > 1) {
            value = "0" + value;
          }

          if (value.length >= 2) {
            let month = value.slice(0, 2);
            if (parseInt(month, 10) > 12) month = "12";
            let year = value.slice(2, 4);
            value = year ? `${month}/${year}` : `${month}/`;
          }

          e.target.value = value;
          field.onChange(value); // Update form state
        }}
      />
    )}
  />
  {errors.exprDate && (
    <span className="text-red-500">{errors.exprDate.message}</span>
  )}
</div>

                          <div className="flex-1">
                            <Label className="text-sm">CVC</Label>
                            <Controller
                              name="CVC"
                              control={control}
                              rules={{
                                validate: (values) => {
                                  const cleaned = values.replace(/\D/g, "");
                                  const valid =
                                    cleaned.length === 3 ||
                                    cleaned.length === 4;
                                  if (!valid) {
                                    return "CVC is required";
                                  }
                                  return true;
                                },
                              }}
                              render={({ field }) => (
                                <Input
                                  type="text"
                                  {...field}
                                  placeholder="enter cvc"
                                  onChange={(e) => {
                                    let value = e.target.value.replace(
                                      /\D/g,
                                      ""
                                    );
                                    if (value.length > 3) {
                                      value = value.slice(0, 4);
                                    }
                                    field.onChange(value);
                                  }}
                                  className="mt-1"
                                />
                              )}
                            />
                            {errors.CVC && (
                              <span className="text-red-500">
                                {errors.CVC.message}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            </div>
            <ButtonPrimary type="submit">Continue</ButtonPrimary>
          </form>

          {/* ==== */}
          <span className="block text-center text-neutral-700 dark:text-neutral-300">
            Already have an account? {` `}
            <Link className="text-green-600" to={`/${merchantname}/login`}>
              Sign in
            </Link>
          </span>
        </div>
      </div>
    </div>
  );
};

export default PageSignUp;
